<template>
  <footer class="pa-4 bg-white border-primary5 flex items-center gap-2">
    <div class="flex justify-start items-center basis-0 flex-grow-1 gap-2">
      <CButton
        v-tooltip="t('canvasUI.collectionPlayer.tableOfContents')"
        class="w-11 h-11"
        :class="{
          'bg-primary5': isOpenTableOfContents,
        }"
        secondary
        size="large"
        @click="toggleTableOfContents()"
      >
        <template #icon>
          <CIcon :fixedWidth="true" icon="grid-2" size="16" />
        </template>
      </CButton>

      <CDropdown
        v-if="pitcherSettings.use_collection_player_with_annotations"
        :options="annotationMenu"
        placement="top-start"
        :show="isVisibleMenu"
        :to="rootElement"
        trigger="manual"
        @clickoutside="handleAnnotationOutsideClick"
        @select="handleAnnotationSelect"
      >
        <CButton
          ref="annotationMenuRef"
          v-tooltip="t('canvasUI.collectionPlayer.annotations.menu')"
          class="w-11 h-11"
          :disabled="!isZoomEnabled"
          secondary
          size="large"
          @click="annotationLayer.toggleMenu()"
        >
          <template #icon>
            <CIcon fixed-width :icon="activeToolIcon" size="16px" />
          </template>
        </CButton>
      </CDropdown>
    </div>

    <div v-if="hasManyGroups" class="flex justify-start self-stretch items-stretch overflow-auto">
      <NButtonGroup class="bg-primary6 px-2 rounded-8" horizontal size="small">
        <CButton
          v-for="group in visibleGroups"
          :key="group.id"
          class="pa-2 important-tracking-[0.25px] font-size-4 font-700 color-primary important-focus-color-primary important-hover-color-primary important-case-capital"
          :class="[activeGroup?.id === group.id ? 'opacity-100' : 'opacity-50']"
          round
          text
          @click="setActiveSlide(group)"
        >
          {{ group.name }}
        </CButton>
      </NButtonGroup>
    </div>

    <div class="flex justify-end items-center basis-0 flex-grow-1 gap-2">
      <CButton
        v-if="fitToWidth"
        v-tooltip="t('canvasUI.collectionPlayer.fitToContent')"
        class="w-11 h-11"
        :disabled="!activeSlide"
        secondary
        size="large"
        @click="toggleFitToWidth()"
      >
        <template #icon>
          <CIcon :fixedWidth="true" icon="maximize" size="16" />
        </template>
      </CButton>

      <CButton
        v-else
        v-tooltip="t('canvasUI.collectionPlayer.fitToWidth')"
        class="w-11 h-11"
        :disabled="!activeSlide"
        secondary
        size="large"
        @click="toggleFitToWidth()"
      >
        <template #icon>
          <CIcon :fixedWidth="true" icon="arrows-left-right-to-line" size="16" />
        </template>
      </CButton>

      <CButton
        v-tooltip="t('canvasUI.collectionPlayer.zoomOut')"
        class="w-11 h-11"
        :disabled="!isZoomEnabled"
        secondary
        size="large"
        @click="zoomOut()"
      >
        <template #icon>
          <CIcon :fixedWidth="true" icon="magnifying-glass-minus" size="16" />
        </template>
      </CButton>

      <CButton
        v-tooltip="t('canvasUI.collectionPlayer.zoomIn')"
        class="w-11 h-11"
        :disabled="!isZoomEnabled"
        secondary
        size="large"
        @click="zoomIn()"
      >
        <template #icon>
          <CIcon :fixedWidth="true" icon="magnifying-glass-plus" size="16" />
        </template>
      </CButton>

      <CButton
        v-if="isFullscreen"
        v-tooltip="t('canvasUI.collectionPlayer.exitFullscreen')"
        class="w-11 h-11"
        size="large"
        type="primary"
        @click="exitFullscreen()"
      >
        <template #icon>
          <CIcon :fixedWidth="true" icon="compress" size="16" />
        </template>
      </CButton>

      <CButton
        v-else
        v-tooltip="t('canvasUI.collectionPlayer.enterFullscreen')"
        class="w-11 h-11"
        size="large"
        type="primary"
        @click="enterFullscreen()"
      >
        <template #icon>
          <CIcon :fixedWidth="true" icon="arrow-up-right-and-arrow-down-left-from-center" size="16" />
        </template>
      </CButton>
    </div>
  </footer>
</template>

<script setup lang="tsx">
import { useI18n } from 'vue-i18n'
import { NButtonGroup } from 'naive-ui'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import { useAppStore } from '@collection-player/stores/app.store'
import { computed, type ComputedRef, inject, onMounted, ref } from 'vue'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'
import { useMarkerjs3 } from '../composables/markerjs.use.ts'
import type * as Markerjs3 from '@markerjs/markerjs3'

const { t } = useI18n()
const {
  visibleGroups,
  activeGroup,
  activeSlide,
  fitToWidth,
  isOpenTableOfContents,
  hasManyGroups,
  zoom: { isZoomEnabled, zoomOut, zoomIn },
  fullscreen: { isFullscreen, enterFullscreen, exitFullscreen },
  setActiveSlide,
  toggleFitToWidth,
  toggleTableOfContents,
  annotationLayer,
  rootElement,
} = useAppStore()
const annotationMenuRef = ref<InstanceType<typeof CButton> | null>(null)
const laserPointerMarker = ref<typeof Markerjs3.MarkerBase | null>(null)
const markerjs3 = ref<typeof Markerjs3 | null>(null)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const { isVisibleMenu, activeTool } = annotationLayer

const FONT_SIZE_CLASS = 'font-size-4'

const activeToolIcon = computed(() => {
  if (!activeTool.value) {
    return markerTools.value.handPointer.icon
  }

  const toolConfig = Object.values(markerTools.value).find((tool) => tool.marker === activeTool.value)

  return toolConfig ? toolConfig.icon : markerTools.value.handPointer.icon
})

const handleAnnotationOutsideClick = (event: Event) => {
  const annotationMenuElement = annotationMenuRef.value?.$el

  if (
    annotationMenuElement &&
    (annotationMenuElement.contains(event.target as HTMLElement) || annotationMenuElement === event.target)
  ) {
    return
  }

  annotationLayer.showMenu(false)
}

const markerTools = computed<{
  [key: string]: {
    marker: typeof Markerjs3.MarkerBase | 'selection' | 'eraseSelected' | 'eraseAll' | null
    icon: string
    translationKey: string
    action?: () => void
    dividerBefore?: boolean
  }
}>(() => ({
  handPointer: {
    marker: null,
    icon: 'hand-pointer',
    translationKey: t('canvasUI.collectionPlayer.annotations.swipe'),
  },
  ...(markerjs3.value
    ? {
        arrow: {
          dividerBefore: true,
          marker: 'selection',
          icon: 'arrow-pointer',
          translationKey: t('canvasUI.collectionPlayer.annotations.selection'),
        },
        pointer: {
          marker: laserPointerMarker.value,
          icon: 'circle-dot',
          translationKey: t('canvasUI.collectionPlayer.annotations.laserPointer'),
        },
        highlighter: {
          marker: markerjs3.value.HighlighterMarker,
          icon: 'highlighter',
          translationKey: t('canvasUI.collectionPlayer.annotations.highlighter'),
        },
        pen: {
          marker: markerjs3.value.FreehandMarker,
          icon: 'pencil-mechanical',
          translationKey: t('canvasUI.collectionPlayer.annotations.pen'),
        },
        eraseSelected: {
          marker: 'eraseSelected',
          icon: 'eraser',
          translationKey: t('canvasUI.collectionPlayer.annotations.eraseSelected'),
          dividerBefore: true,
          action: () => {
            const markerInstance = annotationLayer.activeMarker.value

            if (!markerInstance) return

            markerInstance.deleteSelectedMarkers()
          },
        },
        eraseAll: {
          marker: 'eraseAll',
          icon: 'trash',
          translationKey: t('canvasUI.collectionPlayer.annotations.eraseAll'),
          action: () => {
            const markerInstance = annotationLayer.activeMarker.value

            if (!markerInstance) return

            const editors = [...markerInstance.editors]

            editors.forEach((editor) => {
              markerInstance.deleteMarker(editor)
            })
          },
        },
      }
    : {}),
}))

const annotationMenu = computed<DropdownMixedOption[]>(() => {
  const menuItems = []

  for (const [key, tool] of Object.entries(markerTools.value)) {
    if ('dividerBefore' in tool && tool.dividerBefore) {
      menuItems.push({ key: `d-${key}`, type: 'divider' })
    }

    menuItems.push({
      label: () => <div class={FONT_SIZE_CLASS}>{tool.translationKey}</div>,
      key,
      icon: () => <CIcon icon={tool.icon} />,
    })
  }

  return menuItems
})

const handleAnnotationSelect = (action: string) => {
  annotationLayer.showMenu(false)

  const markerInstance = annotationLayer.activeMarker.value

  if (!markerInstance) {
    console.warn('No active marker instance found')
    return
  }

  const toolConfig = markerTools.value[action]

  if (!toolConfig) {
    console.warn(`Unknown marker type: ${action}`)
    return
  }

  if (toolConfig.action) {
    toolConfig.action()
    annotationLayer.clearSelection()

    return
  }

  annotationLayer.setActiveTool(toolConfig.marker)
  annotationLayer.clearSelection()
}

onMounted(async () => {
  const {
    laserPointer: { LaserPointerMarker },
    markerjs3: Markerjs3,
  } = await useMarkerjs3()

  laserPointerMarker.value = LaserPointerMarker
  markerjs3.value = Markerjs3
})
</script>
